import { FunctionComponent, useEffect, useState, useMemo } from 'react'
import {
  Box,
  Typography,
  Switch,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import useBackendUserStore from '../../stores/backendUser'
import useAuthStore from '../../stores/auth'
import Pill from '../../components/Pill'
import { BackendUser } from '../../types'
import Table from '../../components/Table'
import { toast } from 'react-toastify'

const ManageBackendUsers: FunctionComponent = () => {
  const { backendUsers, getBackendUsers, toggleNotifications } = useBackendUserStore()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    (async () => {
      await getBackendUsers()
      setLoading(false)
    })()
  }, [getBackendUsers, setLoading])

  const styles = useStyles()

  // Filter backend users based on current user's role
  // Admins can only see other admins and employees, not super_admins
  // Super admins can see everyone
  const filteredBackendUsers = useMemo(() => {
    const allUsers = Object.values(backendUsers)

    if (user?.role === 'super_admin') {
      return allUsers
    }

    // Regular admins should not see super_admins
    return allUsers.filter(bu => bu.role !== 'super_admin')
  }, [backendUsers, user?.role])

  const handleNotificationToggle = async (id: string, currentValue: boolean) => {
    try {
      await toggleNotifications(id, !currentValue)
      toast.success('Notification preference updated')
    } catch (error) {
      toast.error('Failed to update notification preference')
    }
  }

  const getRoleName = (role: string) => {
    const roleMap: Record<string, string> = {
      'admin': 'Admin',
      'super_admin': 'Super Admin',
      'employee': 'Employee',
    }
    return roleMap[role] || role
  }

  return (
    <Box p={2}>
      <Box mb={2} className={styles.headingContainer}>
        <Box>
          <Typography variant={'h3'}>
            Notifications
          </Typography>
          <Typography variant={'caption'}>
            Manage email notification preferences for team members. Controls renewal and new subscription notifications.
          </Typography>
        </Box>
      </Box>
      <Box>
        <Table<BackendUser>
          title={'Team Members'}
          rows={filteredBackendUsers}
          loading={loading}
          defaultFiltersOn={false}
          columns={[
            {
              label: 'Email',
              cellFill: (bu: BackendUser) => bu.email,
              searchable: true,
              searchFunction: (bu) => String(bu.email),
              sortable: true,
              csv: true,
              sortValueGetter: (bu) => String(bu.email),
            },
            {
              label: 'Role',
              cellFill: (bu: BackendUser) => (
                <Pill
                  value={getRoleName(bu.role)}
                  color={bu.role === 'super_admin' ? 'blue' : 'green'}
                />
              ),
              searchable: false,
              csv: true,
              csvFill: (bu: BackendUser) => getRoleName(bu.role),
            },
            {
              label: 'Notifications',
              cellFill: (bu: BackendUser) => (
                <Box onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={bu.notificationsOn}
                    onChange={() => handleNotificationToggle(bu.id, bu.notificationsOn)}
                    sx={{
                      '& .MuiSwitch-switchBase': {
                        color: '#fff',
                        '&.Mui-checked': {
                          color: '#fff',
                        },
                        '&.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: '#34C759',
                          opacity: 1,
                        },
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#FF3B30',
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              ),
              searchable: false,
              csv: true,
              csvFill: (bu: BackendUser) => bu.notificationsOn ? 'On' : 'Off',
            },
          ]}
          handleRowClicked={() => {}}
          id={'manage-backend-users'}
        />
      </Box>
    </Box>
  )
}

const useStyles = makeStyles({
  headingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
})

export default ManageBackendUsers
